import moment from 'moment';
import ISelectOption from '../../components/select-form/interfaces/ISelectOption';
import ICompany from '../../interfaces/ICompany';
import ICostCenter from '../../interfaces/ICostCenter';
import IEmployee from '../../interfaces/IEmployee';
import ILocal from '../../interfaces/ILocal';
import ISubsidiary from '../../interfaces/ISubsidiary';
import ITypeDocument from '../../interfaces/ITypeDocument';

export const FOLLOWUP_FILTER_PREFERENCE_SCREEN = 'followup';

const PREFERENCE_VERSION = 1;

export interface IFollowupFilters {
  initialDate: moment.Moment | null;
  finalDate: moment.Moment | null;
  status: ISelectOption[];
  integration: string;
  allowDrafts: boolean;
  typeDocuments: ITypeDocument[];
  company: ICompany | null;
  subsidiary: ISubsidiary | null;
  costCenter: ICostCenter | null;
  local: ILocal | null;
  employee: IEmployee | null;
  protocolNumber: string;
  onlyAlerts: boolean;
}

/** Preferência gravada no localStorage. Datas em ISO; entidades reduzidas ao necessário. */
export interface IFollowupFilterPreference {
  version: number;
  savedAt: string;
  initialDate: string | null;
  finalDate: string | null;
  status: ISelectOption[];
  integration: string;
  allowDrafts: boolean;
  typeDocuments: ITypeDocument[];
  company: ICompany | null;
  subsidiary: ISubsidiary | null;
  costCenter: ICostCenter | null;
  local: ILocal | null;
  employee: IEmployee | null;
  protocolNumber: string;
  onlyAlerts: boolean;
}

export interface IPreferenceSummaryItem {
  label: string;
  value: string;
}

const isObject = (value: any): boolean => !!value && typeof value === 'object' && !Array.isArray(value);

const asString = (value: any, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asBoolean = (value: any, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

const pickEntity = <T extends object>(entity: any, fields: string[]): T | null => {
  if (!isObject(entity) || entity.id === undefined || entity.id === null) return null;
  const picked: any = {};
  fields.forEach(field => {
    if (entity[field] !== undefined && entity[field] !== null) picked[field] = entity[field];
  });
  return picked as T;
};

const COMPANY_FIELDS = ['id', 'code', 'name'];
const SUBSIDIARY_FIELDS = ['id', 'code', 'name', 'companyId'];
const COST_CENTER_FIELDS = ['id', 'code', 'name'];
const LOCAL_FIELDS = ['id', 'code', 'name'];
const EMPLOYEE_FIELDS = ['id', 'code', 'name', 'cpf'];
const TYPE_DOCUMENT_FIELDS = ['id', 'description'];

export const buildFollowupFilterPreference = (filters: IFollowupFilters): IFollowupFilterPreference => ({
  version: PREFERENCE_VERSION,
  savedAt: moment().toISOString(),
  initialDate: filters.initialDate?.isValid() ? filters.initialDate.toISOString() : null,
  finalDate: filters.finalDate?.isValid() ? filters.finalDate.toISOString() : null,
  status: filters.status.map(option => ({ value: option.value, label: option.label })),
  integration: filters.integration,
  allowDrafts: filters.allowDrafts,
  typeDocuments: filters.typeDocuments
    .map(typeDocument => pickEntity<ITypeDocument>(typeDocument, TYPE_DOCUMENT_FIELDS))
    .filter((typeDocument): typeDocument is ITypeDocument => !!typeDocument),
  company: pickEntity<ICompany>(filters.company, COMPANY_FIELDS),
  subsidiary: pickEntity<ISubsidiary>(filters.subsidiary, SUBSIDIARY_FIELDS),
  costCenter: pickEntity<ICostCenter>(filters.costCenter, COST_CENTER_FIELDS),
  local: pickEntity<ILocal>(filters.local, LOCAL_FIELDS),
  employee: pickEntity<IEmployee>(filters.employee, EMPLOYEE_FIELDS),
  protocolNumber: filters.protocolNumber,
  onlyAlerts: filters.onlyAlerts,
});

const sanitizeDate = (value: any): string | null => {
  if (typeof value !== 'string') return null;
  const parsed = moment(value, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.toISOString() : null;
};

export const sanitizeFollowupFilterPreference = (
  raw: any,
  allowedStatus: ISelectOption[],
  allowedIntegration: string[],
): IFollowupFilterPreference | null => {
  if (!isObject(raw) || typeof raw.version !== 'number') return null;

  const allowedStatusByValue = new Map(allowedStatus.map(option => [option.value, option]));
  const status = Array.isArray(raw.status)
    ? raw.status
        .filter((option: any) => isObject(option) && allowedStatusByValue.has(option.value))
        .map((option: any) => allowedStatusByValue.get(option.value) as ISelectOption)
    : [];

  const integration = allowedIntegration.includes(asString(raw.integration)) ? asString(raw.integration) : 'TODOS';

  return {
    version: PREFERENCE_VERSION,
    savedAt: sanitizeDate(raw.savedAt) || '',
    initialDate: sanitizeDate(raw.initialDate),
    finalDate: sanitizeDate(raw.finalDate),
    status,
    integration,
    allowDrafts: asBoolean(raw.allowDrafts),
    typeDocuments: Array.isArray(raw.typeDocuments)
      ? raw.typeDocuments
        .map((typeDocument: any) => pickEntity<ITypeDocument>(typeDocument, TYPE_DOCUMENT_FIELDS))
        .filter((typeDocument: ITypeDocument | null): typeDocument is ITypeDocument => !!typeDocument)
      : [],
    company: pickEntity<ICompany>(raw.company, COMPANY_FIELDS),
    subsidiary: pickEntity<ISubsidiary>(raw.subsidiary, SUBSIDIARY_FIELDS),
    costCenter: pickEntity<ICostCenter>(raw.costCenter, COST_CENTER_FIELDS),
    local: pickEntity<ILocal>(raw.local, LOCAL_FIELDS),
    employee: pickEntity<IEmployee>(raw.employee, EMPLOYEE_FIELDS),
    protocolNumber: asString(raw.protocolNumber),
    onlyAlerts: asBoolean(raw.onlyAlerts),
  };
};

const dateToComparable = (value: moment.Moment | string | null): string => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const comparableFromFilters = (filters: IFollowupFilters) => ({
  initialDate: dateToComparable(filters.initialDate),
  finalDate: dateToComparable(filters.finalDate),
  status: filters.status.map(option => option.value).sort().join('|'),
  integration: filters.integration,
  allowDrafts: filters.allowDrafts,
  typeDocumentIds: filters.typeDocuments.map(typeDocument => typeDocument.id).sort().join('|'),
  companyId: filters.company?.id ?? null,
  subsidiaryId: filters.subsidiary?.id ?? null,
  costCenterId: filters.costCenter?.id ?? null,
  localId: filters.local?.id ?? null,
  employeeId: filters.employee?.id ?? null,
  protocolNumber: filters.protocolNumber.trim(),
  onlyAlerts: filters.onlyAlerts,
});

const comparableFromPreference = (preference: IFollowupFilterPreference) => ({
  initialDate: dateToComparable(preference.initialDate),
  finalDate: dateToComparable(preference.finalDate),
  status: preference.status.map(option => option.value).sort().join('|'),
  integration: preference.integration,
  allowDrafts: preference.allowDrafts,
  typeDocumentIds: preference.typeDocuments.map(typeDocument => typeDocument.id).sort().join('|'),
  companyId: preference.company?.id ?? null,
  subsidiaryId: preference.subsidiary?.id ?? null,
  costCenterId: preference.costCenter?.id ?? null,
  localId: preference.local?.id ?? null,
  employeeId: preference.employee?.id ?? null,
  protocolNumber: preference.protocolNumber.trim(),
  onlyAlerts: preference.onlyAlerts,
});

export const isSameFilterSet = (preference: IFollowupFilterPreference, filters: IFollowupFilters): boolean =>
  JSON.stringify(comparableFromPreference(preference)) === JSON.stringify(comparableFromFilters(filters));

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

const formatCodeName = (entity: { code?: string; name?: string } | null): string => {
  if (!entity) return '';
  return entity.code ? `${entity.code} - ${entity.name || ''}`.trim() : `${entity.name || ''}`.trim();
};

export const formatSavedAt = (savedAt: string): string => {
  const parsed = moment(savedAt);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '';
};

export const describeFollowupFilterPreference = (
  preference: IFollowupFilterPreference,
  integrationOptions: ISelectOption[],
): IPreferenceSummaryItem[] => {
  const items: IPreferenceSummaryItem[] = [];

  const initialDate = formatDate(preference.initialDate);
  const finalDate = formatDate(preference.finalDate);
  if (initialDate && finalDate) items.push({ label: 'Período', value: `${initialDate} a ${finalDate}` });
  else if (initialDate) items.push({ label: 'Período', value: `A partir de ${initialDate}` });
  else if (finalDate) items.push({ label: 'Período', value: `Até ${finalDate}` });

  if (preference.status.length) {
    items.push({ label: 'Situação', value: preference.status.map(option => option.label).join(', ') });
  }

  const integration = integrationOptions.find(option => option.value === preference.integration);
  if (integration) items.push({ label: 'Integração', value: integration.label });

  items.push({ label: 'Rascunhos', value: preference.allowDrafts ? 'Sim' : 'Não' });

  if (preference.typeDocuments.length) {
    items.push({
      label: 'Tipo de documento',
      value: preference.typeDocuments.map(typeDocument => typeDocument.description || '').join(', '),
    });
  }
  if (preference.company) items.push({ label: 'Empresa', value: formatCodeName(preference.company) });
  if (preference.subsidiary) items.push({ label: 'Filial', value: formatCodeName(preference.subsidiary) });
  if (preference.costCenter) items.push({ label: 'Centro de custo', value: formatCodeName(preference.costCenter) });
  if (preference.local) items.push({ label: 'Local', value: formatCodeName(preference.local) });
  if (preference.employee) items.push({ label: 'Colaborador', value: formatCodeName(preference.employee) });
  if (preference.protocolNumber.trim()) {
    items.push({ label: 'Protocolo', value: preference.protocolNumber.trim() });
  }

  items.push({ label: 'Apenas alertas', value: preference.onlyAlerts ? 'Sim' : 'Não' });

  return items.filter(item => !!item.value);
};
